import { BaseAction } from "../../../core/actions/BaseAction"
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil"
import { TrainTypeEnum, TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum"
import { PassengerSetType, AnonymousPassengerSpecification, IBookingResponse } from "../../../Orchestrator/Types/Index"
import { Offer } from "../../../Orchestrator/Types/Types/OffersResponse"
import { findDynamicProduct } from "../../../Orchestrator/Types/Types/TrainTypes"


export class PassengerTicketSummaryAction extends BaseAction<void, PassengerSetType> {

    updatePaymentItems = (outBoundPassengerPriceList: any, atomKey: string) => {
        let allOutBoundInfantCount = 0
        let allOutBoundChildrenCount = 0
        let allOutBoundAdultCount = 0
        let allStudentCount = 0
        let allOutBoundSeniorCitizenCount = 0
        let allWheelchairCount = 0
        let allReducedMobilityCount = 0
        outBoundPassengerPriceList.forEach((p: any) => {
            if (p.pType === "Infant") allOutBoundInfantCount++
            if (p.pType === "Children") allOutBoundChildrenCount++
            if (p.pType === "Student") allStudentCount++
            if (p.pType === "Adult") allOutBoundAdultCount++
            if (p.pType === "Senior Citizen") allOutBoundSeniorCitizenCount++
            if (p.pType === "Wheelchair") allWheelchairCount++
            if (p.pType === "People with Reduced Mobility") allReducedMobilityCount++
        })
        const outBoundPassengerDetails: any = []

        const addPassengerDetail = (count: number, pType: string, label: string) => {
            if (count > 0) {
                const totalAmount = outBoundPassengerPriceList
                    .filter((d: any) => d.pType === pType)
                    .reduce((sum: number, d: any) => sum + (d.pValue || 0), 0);

                outBoundPassengerDetails.push({
                    description: `${count} ${label}`,
                    amount: totalAmount
                });
            }
        };

        // Use common function to handle all types
        addPassengerDetail(allOutBoundAdultCount, "Adult", "Adult");
        addPassengerDetail(allOutBoundChildrenCount, "Children", "Children");
        addPassengerDetail(allOutBoundInfantCount, "Infant", "Infant");
        addPassengerDetail(allOutBoundSeniorCitizenCount, "Senior Citizen", "Senior Citizen (UAE)");
        addPassengerDetail(allStudentCount, "Student", "Student");
        addPassengerDetail(allReducedMobilityCount, "People with Reduced Mobility", "People with Reduced Mobility");
        addPassengerDetail(allWheelchairCount, "Wheelchair", "Wheelchair");
        if (atomKey === "paymentOutboundAtom")
            updateRecoilState(atomKey, outBoundPassengerDetails)
        if (atomKey === "paymentOutboundRefundAtom")
            updateRecoilState(atomKey, outBoundPassengerDetails)
        if (atomKey === "paymentInboundAtom")
            updateRecoilState(atomKey, outBoundPassengerDetails)
        if (atomKey === "paymentInboundRefundAtom")
            updateRecoilState(atomKey, outBoundPassengerDetails)
    }

    calculatePassengerTripSummary(
        offerIds: string[],
        offers: Offer[],
        anonymousPassenger: AnonymousPassengerSpecification[],
        trainType: string,
        passengerSpecificationsTypes: any[],
        isChangeDate: boolean = false,
        booking?: IBookingResponse
    ) {
        const matchedOffers = (offerIds || [])?.map(id => offers.find((d: Offer) => d.offerId === id))?.filter(Boolean) as Offer[];
        const passengerDetailsItems: any[] = []
        const processedPrivacySeatingOffers: Set<string> = new Set();
        if (matchedOffers.length > 0) {
            anonymousPassenger.forEach(passenger => {
                let type = ""
                let passengerGroup = passengerSpecificationsTypes.find(d => d.externalRefId === passenger.externalRef)?.passengerType || ""
                if (passengerGroup.includes("adults")) {
                    type = "Adult"
                } else if (passengerGroup.includes("children")) {
                    type = "Children"
                } else if (passengerGroup.includes("infants")) {
                    type = "Infant"
                } else if (passengerGroup.includes("student")) {
                    type = "Student"
                } else if (passengerGroup.includes("seniorcitizens")) {
                    type = "Senior Citizen"
                } else if (passengerGroup.includes("wheelchair")) {
                    type = "Wheelchair"
                } else if (passengerGroup.includes("reducedMobility")) {
                    type = "People with Reduced Mobility"
                }

                const passengerDetails: any = {
                    pId: passenger.externalRef,
                    pValue: 0,
                    pType: type,
                    pStatus: ""
                }
                // Sum price across all matched offers for this passenger
                let summedNewPrice = 0;
                matchedOffers.forEach(ofr => {
                    const passengerOffer = ofr.admissionOfferParts.find(d => d.passengerRefs.includes(passenger.externalRef));
                    if (passengerOffer?.price) {
                        // check if privacy seating
                        if (passengerOffer.offerMode === "COLLECTIVE") {
                            if (processedPrivacySeatingOffers.has(passengerOffer.id)) {
                                // passengerDetails.pValue = 0;
                                // passengerDetailsItems.push(passengerDetails);
                                return;
                            }
                            processedPrivacySeatingOffers.add(passengerOffer.id);
                        }
                        const newPrice = passengerOffer.price.amount / 100;
                        summedNewPrice += newPrice;
                    }
                });
                if (summedNewPrice >= 0) {
                    let finalPrice = summedNewPrice;
                    // If this is a change-date flow with booking data, subtract original booked price
                    if (isChangeDate && booking?.booking) {
                        const bookingPassenger = booking.booking.passengers.find(p => p.externalRef === passenger.externalRef);
                        if (bookingPassenger) {
                            let originalPrice = 0;
                            const targetDirection = trainType == TrainTypeEnum.outbound ? TripTypeEnum.OUT_BOUND : TripTypeEnum.IN_BOUND;
                            const directionTripIds = new Set(
                                booking.booking.trips.filter(t => t.direction === targetDirection).map(t => t.id)
                            );

                            booking.booking.bookedOffers.forEach(bo => {
                                if (bo.tripCoverage?.coveredTripId && directionTripIds.has(bo.tripCoverage.coveredTripId)) {
                                    bo.admissions?.forEach(adm => {
                                        if (adm.passengerIds.includes(bookingPassenger.id) &&
                                            (adm.status === "FULFILLED" || adm.status === "EXCHANGE_ONGOING")) {
                                            originalPrice += adm.price.amount / 100;
                                        }
                                    });
                                }
                            });
                            finalPrice = summedNewPrice - originalPrice;
                        }
                    }
                    passengerDetails.pValue = finalPrice;
                    passengerDetailsItems.push(passengerDetails);
                }
            })
            if (passengerDetailsItems.length > 0)
                if (trainType === TrainTypeEnum.outbound)
                    this.updatePaymentItems(passengerDetailsItems, "paymentOutboundAtom")
                else
                    this.updatePaymentItems(passengerDetailsItems, "paymentInboundAtom")
        }
    }

    override async run(): Promise<void | null> {
        try {
            const { train, priceClass, offers, offersRequest, type, passengerSpecificationsTypes, isChangeDate, booking } = this.params!;

            let selectedOfferIds: string[] = [];
            if (priceClass) {
                // Try legacy key first, then search dynamic arrays by product code
                let oid = (train?.priceInfo[priceClass as keyof typeof train.priceInfo] as any)?.offerId;
                if (!oid) {
                    const dynProduct = findDynamicProduct(train?.priceInfo, priceClass);
                    oid = dynProduct?.offerId;
                }
                if (Array.isArray(oid)) selectedOfferIds = oid as string[];
                else if (typeof oid === "string" && oid) selectedOfferIds = [oid];
            }

            if (selectedOfferIds.length > 0 && offersRequest?.anonymousPassengerSpecifications) {
                updateRecoilState<string | string[] | null>("selectedOfferIdAtom", selectedOfferIds || null);
                this.calculatePassengerTripSummary(
                    selectedOfferIds,
                    offers,
                    offersRequest.anonymousPassengerSpecifications,
                    type,
                    passengerSpecificationsTypes,
                    isChangeDate,
                    booking
                );
            }

        } catch (error) {
            console.error("Error in PassengerTicketSummaryAction:", error);
        }
    }
}

// Create a singleton instance
export const passengerTicketSummaryAction = new PassengerTicketSummaryAction(); 